/**
 * Pagination Web Component
 * @extends {HTMLElement}
 */
class PaginationComponent extends HTMLElement {
  constructor() {
    super();
    this.currentPage = 1;
    this.totalPages = 1;
    this.paginationList = null;
    this.nextButtonRef = null;
    this.previousButtonRef = null;
    this.isMobile = window.innerWidth < 500.5;
  }

  static get observedAttributes() {
    return ['page', 'totalpage', 'page-param', 'prefix-class', 'offset'];
  }

  connectedCallback() {
    this.#initializeRefs();
    this.#updateAttributes();
    this.#renderPagination();
    this.#addEventListeners();
  }

  /**
   * @param {string} name
   * @param {string|null} oldValue
   * @param {string|null} newValue
   */
  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue !== newValue) {
      this.#updateAttributes();
      this.#renderPagination();
    }
  }

  #initializeRefs() {
    this.paginationList = this.querySelector('.pagination');
    this.nextButtonRef = this.querySelector('[data-ref="next-button"]');
    this.previousButtonRef = this.querySelector('[data-ref="previous-button"]');
  }

  #updateAttributes() {
    const pageAttr = this.getAttribute('page');
    const totalPageAttr = this.getAttribute('totalpage');
    
    this.currentPage = parseInt(pageAttr || '1') || 1;
    this.totalPages = parseInt(totalPageAttr || '1') || 1;
    
    // Ensure page is within valid range
    this.currentPage = Math.max(1, Math.min(this.currentPage, this.totalPages));
  }

  #getPageParam() {
    return this.getAttribute('page-param');
  }

  #getPrefixClass() {
    return this.getAttribute('prefix-class') || '';
  }

  #getOffset() {
    const offsetAttr = this.getAttribute('offset');
    return parseInt(offsetAttr || '1') || 1;
  }

  #getNextButtonHTML() {
    if (this.nextButtonRef) {
      return this.nextButtonRef.innerHTML;
    }
    return this.#getNextIcon();
  }

  #getPreviousButtonHTML() {
    if (this.previousButtonRef) {
      return this.previousButtonRef.innerHTML;
    }
    return this.#getPreviousIcon();
  }

  /**
   * @param {number} page
   * @returns {string|null}
   */
  #generateHref(page) {
    const pageParam = this.#getPageParam();
    if (!pageParam) return null;
    
    const currentUrl = new URL(window.location.href);
    currentUrl.searchParams.set(pageParam, page.toString());
    
    return currentUrl.toString();
  }

  #prefetchNextPage() {
    // Only prefetch if there's a next page and page parameter is available
    if (this.currentPage >= this.totalPages) return;
    
    const pageParam = this.#getPageParam();
    if (!pageParam) return;
    
    const nextPage = this.currentPage + 1;
    const nextPageUrl = this.#generateHref(nextPage);
    
    if (nextPageUrl) {
      // Check if link already exists to avoid duplicate prefetch
      const existingLink = document.querySelector(`link[rel="prefetch"][href="${nextPageUrl}"]`);
      if (!existingLink) {
        const link = document.createElement('link');
        link.rel = 'prefetch';
        link.href = nextPageUrl;
        document.head.appendChild(link);
      }
    }
  }

  #renderPagination() {
    if (!this.paginationList) return;
    if (this.totalPages <= 1) {
      this.hidden = true;
      return;
    }

    // Clear existing content
    this.paginationList.innerHTML = '';

    // Don't render if only one page
    if (this.totalPages <= 1) {
      this.hidden = true;
      return;
    }

    this.hidden = false;
    // Generate pagination items
    const items = this.#generatePaginationItems();
    
    items.forEach(item => {
      const li = document.createElement('li');
      const prefixClass = this.#getPrefixClass();
      const pageParam = this.#getPageParam();
      
      // Apply prefix class to li if provided
      if (prefixClass) {
        li.className = `${prefixClass}-item`;
      } else {
        li.className = 'page-item';
      }
      
      if (item.type === 'button') {
        const href = item.page && pageParam ? this.#generateHref(item.page) : null;
        
        // Create either anchor or button based on href presence
        const element = href ? document.createElement('a') : document.createElement('button');
        
        // Set base classes
        if (prefixClass) {
          element.className = `${prefixClass}-link`;
          if (item.isNext) {
            element.classList.add(`${prefixClass}-link--next`);
          } else if (item.isPrev) {
            element.classList.add(`${prefixClass}-link--prev`);
          }
        } else {
          element.className = 'button button-primary';
        }
        
        // Set href for anchor elements
        if (href && element instanceof HTMLAnchorElement) {
          element.href = href;
        } else if (element instanceof HTMLButtonElement) {
          element.setAttribute('data-page', item.page?.toString() || '1');
        }
        
        // Handle SVG content vs text content
        if (item.text && item.text.includes('<svg')) {
          element.innerHTML = item.text;
        } else {
          element.textContent = item.text || '';
        }
        
        if (item.disabled) {
          if (element instanceof HTMLButtonElement) {
            element.disabled = true;
          }
          element.classList.add('disabled');
        }
        
        if (item.active) {
          element.classList.add('active');
        }
        
        // Add accessible name for numbered pages|
        // Set aria-label if provided
        if (item.ariaLabel) {
          element.setAttribute('aria-label', item.ariaLabel);
        } else {
        const pageNumber = typeof item.page === 'number' ? item.page : null;
        if (pageNumber) {
            element.setAttribute('aria-label', `Page ${pageNumber}`);
          }
        }
        li.setAttribute('role', 'listitem');
        li.appendChild(element);
      } else if (item.type === 'ellipsis') {
        const span = document.createElement('span');
        span.className = prefixClass ? `${prefixClass}-link ${prefixClass}-link--ellipsis` : 'page-link page-link--ellipsis';
        
        span.textContent = '...';
        span.setAttribute('aria-hidden', 'true');
        li.setAttribute('role', 'presentation');
        li.appendChild(span);
      }
      
      if (this.paginationList) {
        this.paginationList.appendChild(li);
      }
    });

    // Prefetch next page if available
    this.#prefetchNextPage();
  }

  #generatePaginationItems() {
    const items = [];
    const current = this.currentPage;
    const total = this.totalPages;
    const offset = this.#getOffset();

    // Previous button (only show if not on first page)
    if (current > 1) {
      items.push({
        type: 'button',
        text: this.#getPreviousButtonHTML(),
        page: current - 1,
        disabled: false,
        ariaLabel: 'Previous',
        isPrev: true
      });
    }
    const isMobile = this.isMobile;
    // Add pages around current page based on offset
    const startPage = Math.max(2, current - offset);
    const endPage = Math.min(total - 1, current + offset);
    // Always show first page
    if (total > 0 && (!isMobile || current - offset <= 1)) {
      items.push({
        type: 'button',
        text: '1',
        page: 1,
        active: current === 1
      });
    }

    // Add ellipsis after first page if current page is far from start
    if (current - offset > 2 && !isMobile) {
      items.push({
        type: 'ellipsis'
      });
    }

    for (let i = startPage; i <= endPage; i++) {
      if (i !== 1 && i !== total) {
        items.push({
          type: 'button',
          text: i.toString(),
          page: i,
          active: current === i
        });
      }
    }

    // Add ellipsis before last page if current page is far from end
    if (total > current + offset + 1 && !isMobile) {
      items.push({
        type: 'ellipsis'
      });
    }

    // Always show last page (if more than 1 page)
    if (total > 1 && (!isMobile || current + offset >= total)) {
      items.push({
        type: 'button',
        text: total.toString(),
        page: total,
        active: current === total
      });
    }

    // Next button (only show if not on last page)
    if (current < total) {
      items.push({
        type: 'button',
        text: this.#getNextButtonHTML(),
        page: current + 1,
        disabled: false,
        ariaLabel: 'Next',
        isNext: true
      });
    }

    return items;
  }

  #getPreviousIcon() {
    return `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
      <polyline points="15,18 9,12 15,6"></polyline>
    </svg>`;
  }

  #getNextIcon() {
    return `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
      <polyline points="9,18 15,12 9,6"></polyline>
    </svg>`;
  }

  #addEventListeners() {
    if (!this.paginationList) return;

    // Add window resize event listener to re-render pagination on viewport changes
    window.addEventListener('resize', () => {
      const wasMobile = this.isMobile;
      this.isMobile = window.innerWidth < 500.5;
      if (wasMobile !== this.isMobile) {
        this.#renderPagination();
      }
    });

    if (this.hasAttribute('data-skip-navigation')) return;
    this.paginationList.addEventListener('click', (event) => {
      const target = event.target;
      if (!target || !(target instanceof Element)) return;
      
      // Handle both button and anchor elements
      const button = target.closest('button[data-page]');
      const anchor = target.closest('a[href]');
      
      // For button elements (when no href is provided)
      if (button && button instanceof HTMLButtonElement && !button.disabled) {
        const pageAttr = button.getAttribute('data-page');
        const page = parseInt(pageAttr || '1');
        if (page && page !== this.currentPage && page >= 1 && page <= this.totalPages) {
          event.preventDefault();
          this.#navigateToPage(page);
        }
      }
      
      // For anchor elements (when href is provided), let the browser handle navigation
      // but still dispatch the pagination:change event for consistency
      if (anchor && anchor instanceof HTMLAnchorElement) {
        const href = anchor.getAttribute('href');
        if (href) {
          // Try to extract page number from the actual href
          const pageFromHref = this.#extractPageFromHref(href);
          if (pageFromHref && pageFromHref !== this.currentPage) {
            this.dispatchEvent(new CustomEvent('pagination:change', {
              detail: { page: pageFromHref, totalPages: this.totalPages },
              bubbles: true
            }));
          }
        }
      }
    });

    // Keyboard navigation
    this.paginationList.addEventListener('keydown', (event) => {
      const keyboardEvent = /** @type {KeyboardEvent} */ (event);
      if (keyboardEvent.key === 'Enter' || keyboardEvent.key === ' ') {
        const target = event.target;
        if (!target || !(target instanceof Element)) return;
        
        const button = target.closest('button[data-page]');
        if (button && button instanceof HTMLButtonElement && !button.disabled) {
          event.preventDefault();
          button.click();
        }
      }
    });
  }

  /**
   * @param {string} href
   * @returns {number|null}
   */
  #extractPageFromHref(href) {
    try {
      const url = new URL(href);
      const pageParam = this.#getPageParam();
      const pageValue = pageParam ? url.searchParams.get(pageParam) : null;
      
      if (pageValue) {
        const page = parseInt(pageValue);
        return isNaN(page) ? null : page;
      }
    } catch (error) {
      // If URL parsing fails, fall back to regex matching
      const pageParam = this.#getPageParam();
      const pageMatch = href.match(new RegExp(`[?&]${pageParam}=(\\d+)`));
      if (pageMatch && pageMatch[1]) {
        return parseInt(pageMatch[1]);
      }
    }
    
    return null;
  }

  /**
   * @param {number} page
   */
  #navigateToPage(page) {
    // Update current page
    this.currentPage = page;
    this.setAttribute('page', page.toString());
    
    // Dispatch custom event
    this.dispatchEvent(new CustomEvent('pagination:change', {
      detail: { page, totalPages: this.totalPages },
      bubbles: true
    }));

    // Re-render pagination
    this.#renderPagination();
  }

  // Public methods for external control
  /**
   * @param {number} page
   */
  setPage(page) {
    if (page >= 1 && page <= this.totalPages) {
      this.#navigateToPage(page);
    }
  }

  /**
   * @param {number} total
   */
  setTotalPages(total) {
    this.totalPages = Math.max(1, total);
    this.setAttribute('totalpage', this.totalPages.toString());
    
    // Ensure current page is still valid
    if (this.currentPage > this.totalPages) {
      this.currentPage = this.totalPages;
      this.setAttribute('page', this.currentPage.toString());
    }
    
    this.#renderPagination();
  }

  getCurrentPage() {
    return this.currentPage;
  }

  getTotalPages() {
    return this.totalPages;
  }
}

customElements.define('pagination-component', PaginationComponent);
